/**
 * One-time repairs to a user's stored calendar settings.
 *
 * `useArcheSettings` persists the WHOLE settings object on every write and
 * loads it as `{ ...defaults, ...stored }`. A stored value therefore always
 * beats a changed default — so correcting a default is not enough on its own
 * for anyone who has saved settings even once.
 *
 * Migrations are keyed off `settingsVersion`, which defaults to 0 so that a
 * blob written before the field existed is recognised as unmigrated.
 */

import type { CalendarSettings } from './types';

/** Bump when adding a migration below. */
export const CALENDAR_SETTINGS_VERSION = 1;

/**
 * The patch needed to bring `settings` up to date, or null when it already is.
 *
 * Pure and idempotent: applying the result and re-running must return null.
 */
export function migrateCalendarSettings(
  settings: CalendarSettings,
): Partial<CalendarSettings> | null {
  const from = settings.settingsVersion ?? 0;
  if (from >= CALENDAR_SETTINGS_VERSION) return null;

  const patch: Partial<CalendarSettings> = { settingsVersion: CALENDAR_SETTINGS_VERSION };

  // v1 — the six toolbar feature toggles shipped defaulting to `false` while
  // nothing read them, so every toolbar button showed regardless. Now that
  // they are honoured, a stored `false` would silently remove six buttons.
  //
  // Forcing them true loses no user intent: until the Features dialog was
  // mounted there was no way for anyone to set them, so every stored `false`
  // is the old default rather than a choice.
  if (from < 1) {
    patch.showToolbarTasks = true;
    patch.showToolbarFocusTime = true;
    patch.showToolbarSchedulingLinks = true;
    patch.showToolbarFindTime = true;
    patch.showToolbarAnalytics = true;
    patch.showToolbarImport = true;
  }

  return patch;
}
